import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from "react";
import type { OSSchema } from "@sloe/schema";
import { Layers, MessagesSquare, Database, ClipboardList, ChevronDown, ChevronRight, X, Check, CalendarClock, Plus, Trash2, Pause, Play, Phone, ListChecks, Target, Wrench, PackageCheck, AlertTriangle, MonitorPlay, CheckCircle2, ShieldCheck, Code2, Image, Video, PenLine } from "lucide-react";
import { ErrorBoundary } from "./ErrorBoundary";
import { isVoiceEnabled } from "../lib/voice-flag";
import AuditScreen from "./schema-renderer/AuditScreen";
import { DEFAULT_BRAND_COLOR } from "../lib/brand";
import { safeInitial } from "../lib/initial";
import type { AssistantProfile } from "../agents/registry";
import { buildAgentWorkbench, buildFailedRunItems, type WorkbenchAssignmentRun, type WorkbenchItem } from "../agents/workbench";

// ─────────────────────────────────────────────────────────────────────────
// AssistantPage — a profile header (avatar, name, engine badge, role line,
// capabilities, tap-to-try starter chips) above a tab surface whose set depends
// on the assistant's tier:
//   - WORKER (is_system === false: Operator, Analyst): Chat / Workspace /
//     Activity. No Connections tab and no Goals/Evolution tab — connections are
//     workspace-wide (os_mcp_connections is per-workspace, shared by every
//     agent at the data layer), and evolution is structural = the Architect's
//     domain, so neither is duplicated per worker.
//   - SYSTEM / "president" (is_system === true: The Architect): Chat / Activity.
//     Integrations + Evolution are workspace-wide system surfaces and now live as
//     standalone "Systems" nav screens in CodeOSShell — not as tabs here.
// Activity is always AuditScreen filtered to this assistant (assistantId). The
// reused surfaces keep their own light styling inside a neutral light panel; the
// chrome is dark to match the shell.
// ─────────────────────────────────────────────────────────────────────────

// Code-split parity with CodeOSShell: keep the heavy Operator surface (threads
// UI + react-markdown + voice tree) out of the boot chunk. Vite dedupes this
// against the shell's identical lazy import into one shared chunk.
const OperatorWithThreads = lazy(() =>
  import("./schema-renderer/OperatorWithThreads").then((m) => ({
    default: m.OperatorWithThreads,
  })),
);

// Voice CALL surface — browser-only + heavy; kept out of the boot chunk.
const VoiceCanvas = lazy(() =>
  import("./voice-mode/VoiceCanvas").then((m) => ({ default: m.VoiceCanvas })),
);

type TabId = "chat" | "studio" | "assignments" | "workspace" | "integrations" | "evolution" | "activity";

type TabDef = { id: TabId; label: string; icon: typeof MessagesSquare };

// Worker assistants (Operator, Analyst). Assignments = user standing orders.
const WORKER_TABS: TabDef[] = [
  { id: "chat", label: "Chat", icon: MessagesSquare },
  { id: "assignments", label: "Assignments", icon: CalendarClock },
  { id: "workspace", label: "Workspace", icon: Database },
  { id: "activity", label: "Activity", icon: ClipboardList },
];

const STUDIO_TAB: TabDef = { id: "studio", label: "Studio", icon: Code2 };

// System/president assistant (The Architect). Integrations + Evolution were
// promoted to standalone "Systems" nav screens (CodeOSShell), so they're gone
// from this tab set — the Architect keeps Chat + Activity.
const SYSTEM_TABS: TabDef[] = [
  { id: "chat", label: "Chat", icon: MessagesSquare },
  { id: "activity", label: "Activity", icon: ClipboardList },
];

export function AssistantPage({
  profile,
  os,
  synthSchema,
  userFirstName,
  brandColor,
  initialTab,
  initialPrompt,
  onAskOperator,
  onNavigateAssistant,
  onConsumeInitialIntent,
  isAssistantAvailable,
  onOpenAgentLibrary,
  onOpenMissionControl,
}: {
  profile: AssistantProfile;
  os: { id: string };
  synthSchema: OSSchema;
  userFirstName?: string;
  brandColor?: string;
  // One-shot deep-link intent from Mission Control: open on a specific tab
  // and/or prefill the Chat composer. Honored on mount — the shell remounts the
  // page per navigation, so a fresh intent re-seeds these initializers.
  initialTab?: string;
  initialPrompt?: string;
  onAskOperator?: (prompt: string) => void;
  onNavigateAssistant?: (assistantId: string, target?: { tab?: string; prompt?: string }) => void;
  onConsumeInitialIntent?: () => void;
  isAssistantAvailable?: (assistantId: string) => boolean;
  onOpenAgentLibrary?: (department?: string) => void;
  onOpenMissionControl?: () => void;
}) {
  const accent = brandColor || DEFAULT_BRAND_COLOR;
  // Tab set depends on the assistant's tier (see file header).
  const tabs = profile.is_system
    ? SYSTEM_TABS
    : profile.studio_enabled
      ? [WORKER_TABS[0], STUDIO_TAB, ...WORKER_TABS.slice(1)]
      : WORKER_TABS;
  const [tab, setTab] = useState<TabId>(() =>
    tabs.some((t) => t.id === initialTab) ? (initialTab as TabId) : "chat",
  );
  // Deep-link prompt seed (from Mission Control / the profile sheet): seeds the
  // composer on mount; cleared when leaving Chat. `chatNonce` remounts the chat
  // so a prompt sent while already on the Chat tab still fires (Operator
  // auto-sends a seeded initialPrompt once per mount). Keep a unique id so a
  // prompt can be bound to ONE thread and never leak into manually-created chats.
  const [chatSeed, setChatSeed] = useState<{ id: number; prompt: string } | null>(null);
  const [chatNonce, setChatNonce] = useState(0);
  const promptSeedRef = useRef(0);
  const initialIntentConsumedRef = useRef(false);
  // Profile sheet (iMessage-style identity panel behind the header tap).
  const [sheetOpen, setSheetOpen] = useState(false);
  // Voice CALL (Slice 1) — worker assistants only. activeThreadId is lifted from
  // OperatorWithThreads so a call leaves receipts in the same thread as the chat.
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  const queueChatPrompt = useCallback((prompt: string) => {
    const trimmed = String(prompt || "").trim();
    if (!trimmed) return;
    promptSeedRef.current += 1;
    setChatSeed({ id: promptSeedRef.current, prompt: trimmed });
  }, []);

  useEffect(() => {
    if (initialIntentConsumedRef.current) return;
    if (!initialPrompt?.trim() && !initialTab) return;
    initialIntentConsumedRef.current = true;
    if (initialPrompt?.trim()) {
      queueChatPrompt(initialPrompt);
    }
    onConsumeInitialIntent?.();
  }, [initialPrompt, initialTab, onConsumeInitialIntent, queueChatPrompt]);

  const goToTab = (next: TabId) => {
    if (next !== "chat") setChatSeed(null);
    setTab(next);
  };

  // Reused by the profile-sheet chips: close the sheet, switch to Chat, and send
  // the prompt (via the seed + remount auto-send path).
  const sendPrompt = (prompt: string) => {
    queueChatPrompt(prompt);
    setChatNonce((n) => n + 1);
    setTab("chat");
    setSheetOpen(false);
  };

  const handleSeededPromptConsumed = useCallback(() => {
    setChatSeed(null);
  }, []);

  const [servingProvider, setServingProvider] = useState<string | null>(null);

  const initial = safeInitial(profile.display_name);

  const activeBadge = profile.engine_badge;

  const chatSpinner = (
    <div className="flex items-center justify-center h-full w-full">
      <div
        className="w-8 h-8 rounded-full border-2 border-zinc-200 animate-spin"
        style={{ borderTopColor: accent }}
      />
    </div>
  );

  return (
    <div className="relative h-full flex flex-col bg-[#0A0A0A] text-white">
      {/* Profile header — ONE elegant row, TAPPABLE: opens the profile sheet
          (identity + capabilities + starter chips). A chevron hints at it. */}
      <div className="shrink-0 px-4 md:px-6 py-3 border-b border-white/10 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          className="group flex items-center gap-3 flex-1 min-w-0 text-left -mx-1 px-1 py-0.5 rounded-lg hover:bg-white/[0.04] transition-colors cursor-pointer"
        >
          <div
            className="w-9 h-9 rounded-lg shrink-0 flex items-center justify-center text-[#0a0a0a] text-[15px] font-bold"
            style={{ background: accent }}
          >
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[15px] font-semibold text-white truncate">{profile.display_name}</h1>
              <ChevronDown size={14} className="shrink-0 text-white/40 group-hover:text-white/75 transition-colors" />
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium text-white/60 bg-white/10 border border-white/10 shrink-0">
                <Layers size={10} />
                {activeBadge}
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-300/90 shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Online
              </span>
            </div>
          </div>
        </button>
        {/* Voice CALL — worker assistants only (Operator / Analyst). Routes a
            spoken turn through the same persona + tools as text and leaves
            receipts in the active thread. */}
        {!profile.is_system && isVoiceEnabled() && (
          <button
            type="button"
            onClick={() => setVoiceOpen(true)}
            aria-label={`Call ${profile.display_name}`}
            title={`Call ${profile.display_name}`}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold transition-colors"
            style={{ border: `1px solid ${accent}40`, background: `${accent}1f`, color: accent }}
          >
            <Phone size={14} />
            Call
          </button>
        )}
      </div>

      {/* Tab bar */}
      <div className="shrink-0 flex items-center gap-1 px-3 md:px-6 border-b border-white/10 overflow-x-auto">
        {tabs.map((t) => {
          const active = tab === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => goToTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-[13px] whitespace-nowrap border-b-2 -mb-px transition-colors ${
                active
                  ? "border-white text-white font-medium"
                  : "border-transparent text-white/50 hover:text-white"
              }`}
            >
              <Icon size={14} className="shrink-0" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content. Dark chrome above; reused surfaces keep their light
          styling inside a neutral light panel. Only the active tab is mounted. */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {tab === "chat" && (
          <ErrorBoundary scope="the Operator">
            <div className="h-full min-h-0 bg-[#0A0A0A] text-white">
              <Suspense fallback={chatSpinner}>
                <OperatorWithThreads
                  key={`chat-${chatNonce}`}
                  schema={synthSchema}
                  workspaceId={os.id}
                  userFirstName={userFirstName}
                  initialPrompt={chatSeed?.prompt}
                  initialPromptToken={chatSeed?.id}
                  onInitialPromptConsumed={handleSeededPromptConsumed}
                  assistantId={profile.id}
                  theme="dark"
                  showChatHeader={false}
                  onServingProviderChange={setServingProvider}
                  intro={{
                    title: profile.display_name,
                    subtitle: profile.role_description,
                    capabilities: profile.capabilities,
                  }}
                  suggestions={profile.starter_prompts.map((s) => s.prompt)}
                  onActiveThreadChange={setActiveThreadId}
                  onNavigateAssistant={onNavigateAssistant}
                  isAssistantAvailable={isAssistantAvailable}
                  onOpenAgentLibrary={onOpenAgentLibrary}
                  onOpenMissionControl={onOpenMissionControl}
                />
              </Suspense>
            </div>
          </ErrorBoundary>
        )}

        {/* Specialist Studio: role-specific workbench for code or creative production. */}
        {tab === "studio" && profile.studio_enabled && (
          <ErrorBoundary scope="Studio">
            {profile.studio_kind === "content" ? (
              <ContentStudio
                accent={accent}
                assistantName={profile.display_name}
                onSendPrompt={sendPrompt}
              />
            ) : (
              <CodingStudio
                accent={accent}
                assistantName={profile.display_name}
                onSendPrompt={sendPrompt}
              />
            )}
          </ErrorBoundary>
        )}

        {/* Worker-only: standing orders this assistant runs on a schedule. */}
        {tab === "assignments" && (
          <ErrorBoundary scope="Assignments">
            <Assignments osId={os.id} assistantId={profile.id} assistantName={profile.display_name} accent={accent} />
          </ErrorBoundary>
        )}

        {/* Worker-only: read-only view of the Agentic Operating System's collections. */}
        {tab === "workspace" && (
          <ErrorBoundary scope="Workspace">
            <WorkspaceCollections
              osId={os.id}
              accent={accent}
              schema={synthSchema}
              profile={profile}
              onSendPrompt={sendPrompt}
            />
          </ErrorBoundary>
        )}

        {tab === "activity" && (
          <ErrorBoundary scope="Activity">
            <div className="h-full overflow-y-auto bg-zinc-50 text-zinc-900">
              <AuditScreen workspaceId={os.id} assistantId={profile.id} />
            </div>
          </ErrorBoundary>
        )}
      </div>

      {/* Profile sheet — overlays the page (absolute over this relative root),
          so it never pushes the chat layout. */}
      {sheetOpen && (
        <ProfileSheet
          profile={profile}
          accent={accent}
          initial={initial}
          onClose={() => setSheetOpen(false)}
          onChip={sendPrompt}
        />
      )}

      {/* Voice CALL surface — full-screen, persona-aware, leaves receipts in the
          active thread. Gated to worker assistants by the button above. */}
      {voiceOpen && (
        <Suspense fallback={null}>
          <VoiceCanvas
            workspaceId={os.id}
            brandColor={accent}
            assistantId={profile.id}
            assistantName={profile.display_name}
            threadId={activeThreadId}
            onExit={() => setVoiceOpen(false)}
          />
        </Suspense>
      )}
    </div>
  );
}

// ─── Profile sheet ───────────────────────────────────────────────────────
// iMessage-style identity panel behind the header tap. Mobile: slide-up bottom
// sheet with backdrop. Desktop: anchored dropdown card under the header. Dark
// glass (#141414, border-white/10), fade/slide-in. Dismiss via backdrop, X, or
// Escape. Tapping a starter chip closes + switches to Chat + sends the prompt.
function ProfileSheet({
  profile,
  accent,
  initial,
  onClose,
  onChip,
}: {
  profile: AssistantProfile;
  accent: string;
  initial: string;
  onClose: () => void;
  onChip: (prompt: string) => void;
}) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setShown(true));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(r);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <>
      {/* Backdrop (within the relative AssistantPage root) */}
      <div
        className={`absolute inset-0 z-40 bg-black/55 backdrop-blur-[2px] transition-opacity duration-200 ${shown ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Sheet — mobile bottom sheet; desktop dropdown anchored under header */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${profile.display_name} profile`}
        className={`absolute z-50 inset-x-0 bottom-0 rounded-t-2xl md:inset-x-auto md:bottom-auto md:top-3 md:left-4 md:w-[380px] md:rounded-2xl bg-[#141414] border border-white/10 shadow-2xl overflow-hidden transition-all duration-200 ease-out ${
          shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div
          className="max-h-[78vh] md:max-h-[70vh] overflow-y-auto px-5 pt-5"
          style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
        >
          {/* Close */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 top-3 w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X size={16} />
          </button>

          {/* Identity */}
          <div className="flex items-center gap-3.5">
            <div
              className="w-14 h-14 rounded-2xl shrink-0 flex items-center justify-center text-[#0a0a0a] text-2xl font-bold shadow-lg"
              style={{ background: accent }}
            >
              {initial}
            </div>
            <div className="min-w-0">
              <h2 className="text-[18px] font-semibold text-white truncate">{profile.display_name}</h2>
              <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium text-white/60 bg-white/10 border border-white/10">
                <Layers size={10} />
                {profile.engine_badge}
              </span>
            </div>
          </div>

          {/* Role */}
          <p className="text-[13.5px] text-white/60 leading-relaxed mt-4">{profile.role_description}</p>

          {/* Capabilities */}
          {profile.capabilities.length > 0 && (
            <ul className="mt-4 space-y-2">
              {profile.capabilities.map((c) => (
                <li key={c} className="flex items-center gap-2.5 text-[13px] text-white/75">
                  <Check size={14} className="shrink-0" style={{ color: accent }} />
                  {c}
                </li>
              ))}
            </ul>
          )}

          {/* Starter chips */}
          {profile.starter_prompts.length > 0 && (
            <>
              <div className="mt-5 mb-2 text-[11px] font-medium uppercase tracking-wider text-white/35">
                Try asking
              </div>
              <div className="flex flex-wrap gap-2">
                {profile.starter_prompts.map((sp) => (
                  <button
                    key={sp.label}
                    type="button"
                    onClick={() => onChip(sp.prompt)}
                    className="px-3 py-1.5 rounded-full text-[12.5px] text-white/80 bg-white/[0.06] border border-white/10 hover:bg-white/10 hover:text-white transition-colors text-left"
                  >
                    {sp.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function ContentStudio({
  accent,
  assistantName,
  onSendPrompt,
}: {
  accent: string;
  assistantName: string;
  onSendPrompt: (prompt: string) => void;
}) {
  const studioActions = [
    {
      title: "Research an audience",
      detail: "Find pains, objections, language, proof gaps, and buying triggers before writing or generating assets.",
      prompt: "Use your Studio to research the target audience for this offer using approved OS context and web research. Return pains, objections, hooks, proof gaps, niche language, and the first creative tests we should run.",
      icon: Target,
    },
    {
      title: "Create campaign copy",
      detail: "Draft hooks, captions, landing-page copy, emails, and ad variants grounded in approved source facts.",
      prompt: "Use your Studio to build copy variants for a launch campaign. Use approved source facts only, separate claims from assumptions, and create hooks, captions, short ad scripts, email copy, and landing-page sections for approval.",
      icon: PenLine,
    },
    {
      title: "Generate image/video briefs",
      detail: "Plan image, reel, talking-head, UGC, and ad assets with model/provider, prompt, approval, and receipt fields.",
      prompt: "Use your Studio to create image and short-video generation briefs for this campaign. Include audience, persona, hook, script, visual direction, lighting direction, grading direction, provider/model recommendation, estimated cost fields, approval state, and performance metrics to track. If rendering is unavailable, still deliver the finished script and brief package directly.",
      icon: Video,
    },
  ];

  const workflow = [
    "Audience research",
    "Message angles",
    "Copy variants",
    "Image/video briefs",
    "Approvals",
    "Performance iteration",
  ];

  return (
    <div className="h-full overflow-y-auto bg-[#0b0904] text-white">
      <div className="mx-auto max-w-6xl px-5 md:px-8 py-8">
        <div className="relative overflow-hidden rounded-[1.6rem] border border-amber-300/20 bg-[radial-gradient(circle_at_top_left,rgba(251,191,36,0.18),transparent_34%),linear-gradient(135deg,rgba(255,255,255,0.08),rgba(255,255,255,0.025))] p-5 md:p-7">
          <div className="absolute right-6 top-6 hidden h-24 w-24 rounded-full bg-amber-300/10 blur-2xl md:block" />
          <div className="relative flex flex-col md:flex-row md:items-start md:justify-between gap-5">
            <div>
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-amber-500/10"
                style={{ background: `linear-gradient(135deg, ${accent}, #f59e0b)`, color: "#090909" }}
              >
                <Image size={22} />
              </div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-amber-200/70">Creative Studio</div>
              <h2 className="mt-2 text-[25px] md:text-[34px] font-semibold tracking-tight">{assistantName}</h2>
              <p className="mt-3 max-w-3xl text-[13.5px] leading-relaxed text-white/60">
                This is your production workspace for public-facing creative work: audience research, tailored messaging,
                UGC angles, scripts, image/video briefs, visual direction, approvals, media receipts, and iteration
                from real performance data. You own the work here end to end.
              </p>
            </div>
            <div className="rounded-2xl border border-amber-200/15 bg-black/30 px-4 py-3 text-[12px] text-amber-50/60 md:max-w-sm">
              Operator can make quick internal visuals. Public creative work stays with you here unless it is code work, structural OS work, or ordinary record entry.
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-3">
          {studioActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.title}
                type="button"
                onClick={() => onSendPrompt(action.prompt)}
                className="group rounded-2xl border border-amber-200/12 bg-white/[0.045] p-4 text-left transition-all hover:-translate-y-0.5 hover:border-amber-200/30 hover:bg-amber-200/[0.07]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-amber-300/12 text-amber-200">
                      <Icon size={17} />
                    </div>
                    <div className="text-[15px] font-semibold text-white">{action.title}</div>
                    <p className="mt-2 text-[12.5px] leading-relaxed text-white/52">{action.detail}</p>
                  </div>
                  <ChevronRight size={16} className="mt-1 text-white/25 group-hover:text-amber-100/70" />
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.035] p-4">
          <div className="flex flex-wrap gap-2">
            {workflow.map((step, index) => (
              <div key={step} className="flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-1.5 text-[12px] text-white/58">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-300/15 text-[10px] font-semibold text-amber-100">
                  {index + 1}
                </span>
                {step}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-white">
              <ShieldCheck size={15} className="text-amber-200" />
              Creative safety contract
            </div>
            <ul className="mt-3 space-y-2 text-[12.5px] leading-relaxed text-white/50">
              <li>Researches audience and niche language before generating public-facing assets.</li>
              <li>Separates approved facts from assumptions, claims, and creative hypotheses.</li>
              <li>Routes image/video generation through approvals, cost fields, provider/model receipts, and performance tracking.</li>
              <li>Iterates from real results instead of endlessly producing generic content.</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-white">
              <MonitorPlay size={15} className="text-amber-200" />
              Good first inputs
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-[12px] text-white/50">
              {["Target audience", "Offer", "Proof", "Brand rules", "UGC examples", "Performance data", "Launch goal", "Competitors"].map((item) => (
                <span key={item} className="rounded-lg border border-white/10 bg-white/[0.035] px-2.5 py-1">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CodingStudio({
  accent,
  assistantName,
  onSendPrompt,
}: {
  accent: string;
  assistantName: string;
  onSendPrompt: (prompt: string) => void;
}) {
  const studioActions = [
    {
      title: "Build a launch website",
      detail: "Turn offer, audience, proof, screenshots, and brand rules into a new site or landing page.",
      prompt: "Open Studio mode. Help me build a launch website from my OS context. First ask for the target audience, offer, proof, brand constraints, and where the site should live. Then propose the safest build/edit plan before any code changes.",
    },
    {
      title: "Modify an existing website",
      detail: "Point to a URL, repo, local folder, or exported site and get a safe edit plan plus preview checks.",
      prompt: "Open Studio mode. I have an existing website I want to modify. Ask me for the URL, repo, or local project folder, then inspect the current site/code path, propose improvements, and verify with preview/build checks before saying it is done.",
    },
    {
      title: "Improve a page or component",
      detail: "Use a screenshot, page name, or component goal to make targeted UI/content/code changes.",
      prompt: "Open Studio mode. Help me improve one page or component. Ask for the page/component and success goal, then make a small verified change with a clear diff, preview result, and rollback-safe summary.",
    },
  ];

  return (
    <div className="h-full overflow-y-auto bg-[#0A0A0A] text-white">
      <div className="mx-auto max-w-5xl px-5 md:px-8 py-8">
        <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                style={{ background: `${accent}1f`, color: accent }}
              >
                <Code2 size={21} />
              </div>
              <h2 className="text-[24px] md:text-[30px] font-semibold tracking-tight">Studio</h2>
              <p className="mt-2 max-w-2xl text-[13.5px] leading-relaxed text-white/55">
                {assistantName} owns website, app, repo, landing page, and code-bundle work. Point it at a URL,
                GitHub repo, local project folder, screenshot, or brief; it should plan, edit, preview, test, and
                leave receipts instead of guessing.
              </p>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/25 px-4 py-3 text-[12px] text-white/45 md:max-w-xs">
              Only Coding Agent gets this tab. Other agents can recommend code work, but they should route you here.
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-3">
          {studioActions.map((action) => (
            <button
              key={action.title}
              type="button"
              onClick={() => onSendPrompt(action.prompt)}
              className="group rounded-2xl border border-white/10 bg-white/[0.035] p-4 text-left transition-all hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.06]"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[15px] font-semibold text-white">{action.title}</div>
                  <p className="mt-2 text-[12.5px] leading-relaxed text-white/50">{action.detail}</p>
                </div>
                <ChevronRight size={16} className="mt-0.5 text-white/25 group-hover:text-white/60" />
              </div>
            </button>
          ))}
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-white">
              <ShieldCheck size={15} style={{ color: accent }} />
              Safe coding contract
            </div>
            <ul className="mt-3 space-y-2 text-[12.5px] leading-relaxed text-white/50">
              <li>Starts from an approved URL, repo, local folder, screenshot, or brief.</li>
              <li>Shows the edit plan before risky changes.</li>
              <li>Verifies with preview, build, typecheck, or browser checks when available.</li>
              <li>Reports changed files, tests run, remaining risks, and deployment next step.</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-white">
              <MonitorPlay size={15} style={{ color: accent }} />
              Good first inputs
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-[12px] text-white/50">
              {["Website URL", "GitHub repo", "Local folder path", "Screenshot", "Landing page brief", "Brand rules"].map((item) => (
                <span key={item} className="rounded-lg border border-white/10 bg-white/[0.035] px-2.5 py-1">
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Workspace tab — read-only list of the Agentic Operating System's collections + row counts ───
// A code Agentic Operating System has no schema.screens, so the only source of truth for "what
// collections exist" is the distinct screen_id values in screen_data. We read
// them directly via the browser data client (the owner_all RLS policy on
// screen_data scopes to the signed-in owner's Operating Systems (Applications)) and count client-side.
// v1 keeps it simple — it pulls screen_id for every row; fine at current data
// volumes. A dedicated count endpoint is the obvious follow-up if Operating Systems (Applications) grow.
function humanizeScreenId(id: string): string {
  return id
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function WorkspaceCollections({
  osId,
  accent,
  schema,
  profile,
  onSendPrompt,
}: {
  osId: string;
  accent: string;
  schema: OSSchema;
  profile: AssistantProfile;
  onSendPrompt: (prompt: string) => void;
}) {
  const [collections, setCollections] = useState<{ screen_id: string; count: number }[] | null>(null);
  const [actions, setActions] = useState<Array<{
    id: string;
    tool_called: string;
    claim_summary?: string | null;
    tool_response?: any;
    created_at?: string;
  }> | null>(null);
  const [assignmentRuns, setAssignmentRuns] = useState<WorkbenchAssignmentRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedItem, setExpandedItem] = useState<(WorkbenchItem & { section: string }) | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // SF-3 — owner-scoped per-screen counts via the server (cookie carries
      // auth). The old direct anon `screen_data` read returned EMPTY under RLS
      // (auth.uid()=null) with no error, so collections were permanently empty.
      try {
        const res = await fetch(
          `/api/screen-data/${encodeURIComponent(osId)}/counts`,
          { credentials: "include" },
        );
        if (cancelled) return;
        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.ok) {
          setError(json?.error || "Couldn't load collections.");
          setCollections([]);
          return;
        }
        const counts: Record<string, number> = json.counts || {};
        const list = Object.entries(counts)
          .map(([screen_id, count]) => ({ screen_id, count: Number(count) || 0 }))
          .sort((a, b) => b.count - a.count);
        setCollections(list);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || "Couldn't load collections.");
        setCollections([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [osId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/audit/actions?workspace_id=${encodeURIComponent(osId)}&assistant_id=${encodeURIComponent(profile.id)}`,
          { credentials: "include" },
        );
        const json = await res.json().catch(() => null);
        if (!cancelled) setActions(Array.isArray(json?.actions) ? json.actions : []);
      } catch {
        if (!cancelled) setActions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [osId, profile.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/assignments?workspace_id=${encodeURIComponent(osId)}&assistant_id=${encodeURIComponent(profile.id)}`,
          { credentials: "include" },
        );
        const json = await res.json().catch(() => null);
        if (!cancelled) setAssignmentRuns(Array.isArray(json?.assignments) ? json.assignments : []);
      } catch {
        if (!cancelled) setAssignmentRuns([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [osId, profile.id]);

  const countByScreen = new Map((collections || []).map((c) => [c.screen_id, c.count]));
  const schemaScreens = Array.isArray(schema.screens) ? schema.screens : [];
  const visibleCollections = schemaScreens.length > 0
    ? schemaScreens.map((screen) => ({
        screen_id: screen.screen_id,
        label: screen.label || humanizeScreenId(screen.screen_id),
        layout: screen.layout,
        count: countByScreen.get(screen.screen_id) || 0,
        requiredFields: (screen.fields || []).filter((f) => f.required).map((f) => f.label || f.key).slice(0, 3),
      }))
    : (collections || []).map((c) => ({
        screen_id: c.screen_id,
        label: humanizeScreenId(c.screen_id),
        layout: "data",
        count: c.count,
        requiredFields: [] as string[],
      }));
  const roleLine = profile.id === "analyst"
    ? "I read these collections to spot patterns, risks, and next-best moves."
    : profile.id === "operator"
      ? "I can read these collections, create records, update records, and help move work forward."
      : "I use these collections to understand what structure should evolve next.";
  const workbench = buildAgentWorkbench(profile, schema, countByScreen);
  const realFailedRuns = buildFailedRunItems(actions, assignmentRuns);
  const realOutputs: WorkbenchItem[] = (actions || [])
    .filter((action) => !action.tool_response?.error && !/fail|failed|error/i.test(String(action.claim_summary || "")))
    .slice(0, 3)
    .map((action) => ({
      title: action.claim_summary || action.tool_called.replace(/[_-]+/g, " "),
      detail: action.tool_response?.media_request?.brief
        ? action.tool_response.media_request.brief
        : action.tool_response?.next_step
          ? action.tool_response.next_step
          : action.created_at ? new Date(action.created_at).toLocaleString() : "Recent agent output",
      status: action.tool_response?.status === "needs_connector" ? "needs_data" : "ready",
    }));
  const outputItems = realOutputs.length > 0 ? realOutputs : workbench.outputs;
  const failedRunItems = realFailedRuns.length > 0 ? realFailedRuns : workbench.failedRuns;

  return (
    <div className="h-full overflow-y-auto px-5 md:px-8 py-6">
      <h2 className="text-[15px] font-semibold text-white mb-1">Workspace collections</h2>
      <p className="text-[13px] text-white/50 mb-5">
        {roleLine}
      </p>

      <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr] mb-6">
        <WorkbenchSection
          title="Current focus"
          icon={Target}
          accent={accent}
          items={workbench.currentFocus}
          onOpen={(item) => setExpandedItem({ ...item, section: "Current focus" })}
        />
        <WorkbenchSection
          title="Skills loaded"
          icon={Wrench}
          accent={accent}
          items={workbench.skills.length ? workbench.skills : [{
            title: "Base assistant skills",
            detail: "This agent needs a skill pack attached by deployment.",
            status: "missing",
          }]}
          onOpen={(item) => setExpandedItem({ ...item, section: "Skills loaded" })}
        />
      </div>

      <div className="mb-6">
        <WorkbenchSection
          title="Starter actions"
          icon={Play}
          accent={accent}
          items={workbench.starterActions}
          onOpen={(item) => setExpandedItem({ ...item, section: "Starter actions" })}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-3 mb-6">
        <WorkbenchSection title="Data needed" icon={Database} accent={accent} items={workbench.dataNeeds} compact onOpen={(item) => setExpandedItem({ ...item, section: "Data needed" })} />
        <WorkbenchSection title="Outputs" icon={MonitorPlay} accent={accent} items={outputItems} compact onOpen={(item) => setExpandedItem({ ...item, section: "Outputs" })} />
        <WorkbenchSection title="Failed runs" icon={AlertTriangle} accent={accent} items={failedRunItems} compact onOpen={(item) => setExpandedItem({ ...item, section: "Failed runs" })} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1fr_0.9fr] mb-7">
        <WorkbenchSection title="Recommended screens" icon={PackageCheck} accent={accent} items={workbench.recommendedScreens} onOpen={(item) => setExpandedItem({ ...item, section: "Recommended screens" })} />
        <div className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-4">
          <div className="mb-3 flex items-center gap-2">
            <CheckCircle2 size={15} style={{ color: accent }} />
            <h3 className="text-[12px] font-semibold uppercase tracking-wider text-white/45">Successful run means</h3>
          </div>
          <ul className="space-y-2">
            {workbench.successMetrics.slice(0, 4).map((metric) => (
              <li key={metric} className="flex gap-2 text-[12.5px] leading-relaxed text-white/55">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: accent }} />
                <span>{metric}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {collections === null && (
        <div className="text-[13px] text-white/40">Loading collections…</div>
      )}

      {error && (
        <div className="text-[13px] text-red-300">Couldn't load collections: {error}</div>
      )}

      {collections !== null && !error && visibleCollections.length === 0 && (
        <div className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-6 text-center text-[13px] text-white/50">
          No collections yet. The Architect will show expected screens here once the Agentic Operating System has a schema.
        </div>
      )}

      {collections !== null && visibleCollections.length > 0 && (
        <ul className="grid gap-2 sm:grid-cols-2">
          {visibleCollections.map((c) => (
            <li
              key={c.screen_id}
              className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div
                    className="w-8 h-8 rounded-md shrink-0 flex items-center justify-center"
                    style={{ background: `${accent}22` }}
                  >
                    <Database size={15} style={{ color: accent }} />
                  </div>
                  <div className="min-w-0">
                    <span className="block text-[13.5px] text-white/85 truncate">
                      {c.label}
                    </span>
                    <span className="block text-[11px] text-white/35 uppercase tracking-wider mt-0.5">
                      {c.layout || "collection"}
                    </span>
                  </div>
                </div>
                <span className="shrink-0 text-[12px] font-medium text-white/60 tabular-nums">
                  {c.count} {c.count === 1 ? "row" : "rows"}
                </span>
              </div>
              {c.requiredFields.length > 0 ? (
                <div className="mt-3 flex items-center gap-2 text-[12px] text-white/45">
                  <ListChecks size={13} className="shrink-0" style={{ color: accent }} />
                  Needs {c.requiredFields.join(", ")}
                </div>
              ) : (
                <div className="mt-3 text-[12px] text-white/35">Ready for connected data.</div>
              )}
            </li>
          ))}
        </ul>
      )}

      {expandedItem && (
        <WorkbenchDetailCard
          item={expandedItem}
          accent={accent}
          assistantName={profile.display_name}
          onClose={() => setExpandedItem(null)}
          onAction={(prompt) => {
            setExpandedItem(null);
            onSendPrompt(prompt);
          }}
        />
      )}
    </div>
  );
}

function statusLabel(status: WorkbenchItem["status"]): string {
  if (status === "ready") return "Ready";
  if (status === "missing") return "Missing";
  if (status === "needs_data") return "Needs data";
  if (status === "watching") return "Watching";
  return "Planned";
}

function emptyWorkbenchItem(section: string): WorkbenchItem {
  if (section === "Starter actions") {
    return {
      title: "No starter actions configured",
      detail: "This agent needs a deployment contract with starter tasks, success criteria, and failure recovery.",
      status: "missing",
    };
  }
  if (section === "Data needed") {
    return {
      title: "Waiting for workspace schema",
      detail: "Once the Architect knows the main records, this section shows the exact tables, fields, or files this agent needs.",
      status: "watching",
    };
  }
  if (section === "Outputs") {
    return {
      title: "No outputs yet",
      detail: "Tables, charts, summaries, generated assets, and saved answers appear here after this agent completes work.",
      status: "watching",
    };
  }
  if (section === "Failed runs") {
    return {
      title: "No failed runs tracked",
      detail: "If a task fails, this section will show the missing data, tool, permission, or skill needed to finish it.",
      status: "ready",
    };
  }
  if (section === "Recommended screens") {
    return {
      title: "No screen gaps detected",
      detail: "When this agent repeats work often enough, it will recommend a screen and stage it as a Decision.",
      status: "watching",
    };
  }
  return {
    title: "Nothing queued yet",
    detail: "This area will fill in as the agent runs, connects data, and learns which work repeats.",
    status: "watching",
  };
}

function WorkbenchSection({
  title,
  icon: Icon,
  accent,
  items,
  compact = false,
  onOpen,
}: {
  title: string;
  icon: typeof Database;
  accent: string;
  items: WorkbenchItem[];
  compact?: boolean;
  onOpen?: (item: WorkbenchItem) => void;
}) {
  const visibleItems = items.length > 0 ? items : [emptyWorkbenchItem(title)];
  return (
    <section className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-4">
      <div className="mb-3 flex items-center gap-2">
        <Icon size={15} style={{ color: accent }} />
        <h3 className="text-[12px] font-semibold uppercase tracking-wider text-white/45">{title}</h3>
      </div>
      <div className={compact ? "grid gap-2" : "grid gap-2 sm:grid-cols-2"}>
        {visibleItems.slice(0, compact ? 3 : 4).map((item) => (
          <button
            key={`${item.title}-${item.detail}`}
            type="button"
            onClick={() => onOpen?.(item)}
            className="group rounded-md border border-white/10 bg-[#0F0F0F] px-3 py-3 text-left transition-all hover:border-white/25 hover:bg-white/[0.045]"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-white/85 leading-snug">{item.title}</div>
                <div className="mt-1 text-[12px] leading-relaxed text-white/45">{item.detail}</div>
              </div>
              {item.status && (
                <span
                  className="shrink-0 rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] font-medium text-white/45"
                  style={item.status === "missing" ? { color: accent } : undefined}
                >
                  {statusLabel(item.status)}
                </span>
              )}
            </div>
            <div className="mt-3 inline-flex items-center gap-1 text-[11.5px] font-semibold text-white/35 group-hover:text-white/80">
              Review next step
              <ChevronRight size={12} />
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function workbenchActionLabel(item: WorkbenchItem & { section: string }): string {
  if (item.section === "Starter actions") return "Run action";
  if (item.section === "Recommended screens" && item.status === "missing") return "Create Decision";
  if (item.status === "needs_data" || item.section === "Data needed") return "Find data source";
  if (item.section === "Failed runs") return "Diagnose failure";
  if (item.section === "Outputs") return "Generate output";
  return "Run with agent";
}

function workbenchActionPrompt(item: WorkbenchItem & { section: string }, assistantName: string): string {
  if (item.actionPrompt) return item.actionPrompt;
  if (item.section === "Recommended screens" && item.status === "missing") {
    return `Create a Decision to add the "${item.title}" screen. Explain why ${assistantName} needs it, what data it should hold, and how it improves repeatable work.`;
  }
  if (item.status === "needs_data" || item.section === "Data needed") {
    return `Help me connect or grant permission for "${item.title}". Ask where this data lives, then use the right approved connection path.`;
  }
  if (item.section === "Failed runs") {
    return `Diagnose this failed run: "${item.title}". Tell me why it failed, what permission/data/tool/skill is missing, and the smallest next step to complete it.`;
  }
  if (item.section === "Outputs") {
    return `Produce the "${item.title}" output. Use real workspace data if available; if data is missing, tell me the exact source or permission needed.`;
  }
  return `Run this ${assistantName} workspace focus: "${item.title}". Use real workspace data where possible and tell me the next concrete action.`;
}

function WorkbenchDetailCard({
  item,
  accent,
  assistantName,
  onClose,
  onAction,
}: {
  item: WorkbenchItem & { section: string };
  accent: string;
  assistantName: string;
  onClose: () => void;
  onAction: (prompt: string) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#141414] shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-4 border-b border-white/10 px-5 py-4">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-white/35">{item.section}</div>
            <h3 className="mt-1 text-[19px] font-semibold text-white">{item.title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-2 text-white/40 hover:bg-white/10 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-5">
          <p className="text-[14px] leading-relaxed text-white/65">{item.detail}</p>
          <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.035] px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-white/35">What happens next</div>
            <p className="mt-1 text-[13px] leading-relaxed text-white/55">
              {item.section === "Starter actions"
                ? "This opens the agent chat with the exact task. The agent should either complete it with approved data or explain the smallest missing permission, connector, screen, skill, or Decision."
                : item.status === "missing"
                ? "The agent will turn this gap into a Decision or permission request so the Agentic Operating System can track it."
                : item.status === "needs_data"
                  ? "The agent will ask where the data lives and route you to the right approved connection path."
                  : "The agent will try the run, show the result in chat, and record the action in Activity."}
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:justify-end gap-2 border-t border-white/10 bg-white/[0.025] px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-white/10 px-4 py-2 text-[13px] font-semibold text-white/60 hover:bg-white/10 hover:text-white"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={() => onAction(workbenchActionPrompt(item, assistantName))}
            className="rounded-lg px-4 py-2 text-[13px] font-semibold text-[#0a0a0a] transition-transform hover:scale-[1.02]"
            style={{ background: accent }}
          >
            {workbenchActionLabel(item)}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Assignments tab — user standing orders for this assistant ─────────────
// Lists standing orders (instruction, human cadence, last/next run, pause,
// delete) + a plain-language composer with a preset cadence picker (no cron).
// Reads/writes /api/assignments; degrades to an empty state if the table isn't
// migrated yet (the API returns ok:true with []). Premium dark design.
type CadenceKind = "daily" | "weekly" | "monthly";
interface AssignmentCadence { kind: CadenceKind; time: string; dow?: number; dom?: number }
interface AssignmentRow {
  id: string;
  assistant_id: string;
  instruction: string;
  cadence: AssignmentCadence;
  timezone: string;
  active: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
}

const DOW_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
// The viewer's own timezone — used to decide when to spell out an assignment's
// zone (only when it differs, so same-zone owners aren't nagged).
const BROWSER_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
})();
// "Europe/Lisbon" → "Lisbon", "America/Los_Angeles" → "Los Angeles", "UTC" → "UTC".
function tzShort(tz: string): string {
  if (!tz) return "";
  if (tz === "UTC") return "UTC";
  return (tz.split("/").pop() || tz).replace(/_/g, " ");
}
function cadenceLabel(c: AssignmentCadence, tz?: string): string {
  // Run time is in the assignment's zone — append a short zone label when it
  // differs from the viewer's (e.g. "Every day · 18:33 Lisbon").
  const zone = tz && tz !== BROWSER_TZ ? ` ${tzShort(tz)}` : "";
  const t = `${c.time}${zone}`;
  if (c.kind === "weekly") return `Every ${DOW_LABELS[c.dow ?? 1]} · ${t}`;
  if (c.kind === "monthly") return `Monthly on the ${ordinal(c.dom ?? 1)} · ${t}`;
  return `Every day · ${t}`;
}
function relTime(iso: string | null): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = then - Date.now();
  const past = diff < 0;
  const s = Math.abs(diff) / 1000;
  const fmt = s < 60 ? "just now"
    : s < 3600 ? `${Math.round(s / 60)}m`
    : s < 86400 ? `${Math.round(s / 3600)}h`
    : `${Math.round(s / 86400)}d`;
  if (fmt === "just now") return fmt;
  return past ? `${fmt} ago` : `in ${fmt}`;
}

function Assignments({
  osId, assistantId, assistantName, accent,
}: { osId: string; assistantId: string; assistantName: string; accent: string }) {
  const [rows, setRows] = useState<AssignmentRow[] | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [kind, setKind] = useState<CadenceKind>("weekly");
  const [time, setTime] = useState("09:00");
  const [dow, setDow] = useState(5); // Friday
  const [dom, setDom] = useState(1);
  // Timezone: defaultTz is the Charter default (from GET, same as the create
  // API). tz "" = follow the default; the override select sets it explicitly.
  const [defaultTz, setDefaultTz] = useState(BROWSER_TZ);
  const [tz, setTz] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const effTz = tz || defaultTz;
  const tzOptions = Array.from(new Set([defaultTz, BROWSER_TZ, "UTC"].filter(Boolean)));

  const load = async () => {
    try {
      const r = await fetch(`/api/assignments?workspace_id=${encodeURIComponent(osId)}&assistant_id=${encodeURIComponent(assistantId)}`);
      const d = await r.json();
      setRows(Array.isArray(d?.assignments) ? d.assignments : []);
      if (d?.default_timezone) setDefaultTz(d.default_timezone);
    } catch {
      setRows([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [osId, assistantId]);

  const resetComposer = () => { setInstruction(""); setKind("weekly"); setTime("09:00"); setDow(5); setDom(1); setTz(""); setErr(null); };

  const create = async () => {
    const instr = instruction.trim();
    if (!instr) { setErr("Add an instruction."); return; }
    const cadence: AssignmentCadence = kind === "weekly" ? { kind, time, dow }
      : kind === "monthly" ? { kind, time, dom } : { kind, time };
    setSaving(true); setErr(null);
    try {
      const r = await fetch("/api/assignments", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspace_id: osId, assistant_id: assistantId, instruction: instr, cadence, timezone: effTz }),
      });
      const d = await r.json();
      if (!d?.ok) {
        setErr(d?.error === "limit_reached" ? `You've hit the limit of ${d.limit} active assignments.`
          : d?.error === "assignments_unavailable" ? "Assignments aren't enabled yet — try again shortly."
          : d?.error || "Couldn't save.");
        return;
      }
      setComposerOpen(false); resetComposer(); load();
    } catch {
      setErr("Couldn't save — network error.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (a: AssignmentRow) => {
    setRows((prev) => prev?.map((x) => x.id === a.id ? { ...x, active: !x.active } : x) ?? prev);
    await fetch(`/api/assignments/${a.id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ active: !a.active }),
    }).catch(() => {});
    load();
  };
  const del = async (a: AssignmentRow) => {
    setRows((prev) => prev?.filter((x) => x.id !== a.id) ?? prev);
    await fetch(`/api/assignments/${a.id}`, { method: "DELETE" }).catch(() => {});
  };

  const seg = (k: CadenceKind, label: string) => (
    <button
      type="button"
      onClick={() => setKind(k)}
      className={`flex-1 px-3 py-1.5 rounded-md text-[12.5px] font-medium transition-colors ${
        kind === k ? "text-[#0a0a0a]" : "text-white/60 hover:text-white"
      }`}
      style={kind === k ? { background: accent } : undefined}
    >
      {label}
    </button>
  );

  return (
    <div className="h-full overflow-y-auto px-5 md:px-8 py-6 bg-[#0A0A0A] text-white">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-[15px] font-semibold text-white">Assignments</h2>
        {!composerOpen && (
          <button
            type="button"
            onClick={() => { resetComposer(); setComposerOpen(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold text-[#0a0a0a]"
            style={{ background: accent }}
          >
            <Plus size={14} /> New
          </button>
        )}
      </div>
      <p className="text-[13px] text-white/50 mb-5">Standing orders {assistantName} runs on a schedule — without you asking.</p>

      {/* Composer */}
      {composerOpen && (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 mb-5">
          <textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            rows={2}
            placeholder={`e.g. "Send me a pipeline report" or "Chase missing documents"`}
            className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3.5 py-2.5 text-[14px] text-white placeholder-white/30 focus:outline-none focus:border-white/25 resize-none"
          />
          <div className="flex items-center gap-1 mt-3 p-1 rounded-lg bg-white/[0.04] border border-white/10">
            {seg("daily", "Daily")}{seg("weekly", "Weekly")}{seg("monthly", "Monthly")}
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <label className="text-[12px] text-white/45">At</label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="bg-white/[0.06] border border-white/10 rounded-md px-2.5 py-1.5 text-[13px] text-white focus:outline-none focus:border-white/25 [color-scheme:dark]"
            />
            {/* Timezone — the run time is interpreted in this zone. Defaults to
                the Charter's; override to your device's zone or UTC. */}
            <span className="text-white/25">·</span>
            <select
              value={effTz}
              onChange={(e) => setTz(e.target.value)}
              title="Timezone the run time is interpreted in"
              className="bg-white/[0.06] border border-white/10 rounded-md px-2.5 py-1.5 text-[13px] text-white focus:outline-none focus:border-white/25 max-w-[170px]"
            >
              {tzOptions.map((z) => (
                <option key={z} value={z} className="bg-[#141414]">{z}</option>
              ))}
            </select>
            {kind === "weekly" && (
              <select
                value={dow}
                onChange={(e) => setDow(+e.target.value)}
                className="bg-white/[0.06] border border-white/10 rounded-md px-2.5 py-1.5 text-[13px] text-white focus:outline-none focus:border-white/25"
              >
                {DOW_LABELS.map((d, i) => <option key={i} value={i} className="bg-[#141414]">{d}</option>)}
              </select>
            )}
            {kind === "monthly" && (
              <select
                value={dom}
                onChange={(e) => setDom(+e.target.value)}
                className="bg-white/[0.06] border border-white/10 rounded-md px-2.5 py-1.5 text-[13px] text-white focus:outline-none focus:border-white/25"
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n} className="bg-[#141414]">{ordinal(n)}</option>
                ))}
              </select>
            )}
          </div>
          {err && <div className="text-[12.5px] text-red-300 mt-3">{err}</div>}
          <div className="flex items-center gap-2 mt-4">
            <button
              type="button"
              onClick={create}
              disabled={saving || !instruction.trim()}
              className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold text-[#0a0a0a] disabled:opacity-40"
              style={{ background: accent }}
            >
              {saving ? "Saving…" : "Create assignment"}
            </button>
            <button
              type="button"
              onClick={() => { setComposerOpen(false); resetComposer(); }}
              className="px-3.5 py-2 rounded-lg text-[12.5px] font-medium text-white/60 hover:text-white hover:bg-white/[0.06]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {rows === null && (
        <div className="grid gap-2.5">
          <div className="h-[64px] rounded-xl bg-white/[0.05] animate-pulse" />
          <div className="h-[64px] rounded-xl bg-white/[0.05] animate-pulse" />
        </div>
      )}

      {/* Empty state */}
      {rows !== null && rows.length === 0 && !composerOpen && (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-6 py-10 text-center">
          <div className="w-12 h-12 rounded-xl mx-auto flex items-center justify-center mb-4" style={{ background: `${accent}1f` }}>
            <CalendarClock size={22} style={{ color: accent }} />
          </div>
          <p className="text-[14px] text-white/80 font-medium">Give {assistantName} a standing order</p>
          <p className="text-[13px] text-white/50 mt-1.5 max-w-sm mx-auto leading-relaxed">
            Work that happens without you asking — a Friday pipeline report, a Monday document chase. Set it once.
          </p>
          <button
            type="button"
            onClick={() => { resetComposer(); setComposerOpen(true); }}
            className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12.5px] font-semibold text-[#0a0a0a]"
            style={{ background: accent }}
          >
            <Plus size={14} /> New assignment
          </button>
        </div>
      )}

      {/* List */}
      {rows !== null && rows.length > 0 && (
        <ul className="grid gap-2.5">
          {rows.map((a) => (
            <li
              key={a.id}
              className={`rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 ${a.active ? "" : "opacity-55"}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] text-white font-medium leading-snug">{a.instruction}</div>
                  <div className="flex items-center gap-2 mt-1.5 text-[12px] text-white/45 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-white/60">
                      <CalendarClock size={12} style={{ color: accent }} />
                      {cadenceLabel(a.cadence, a.timezone)}
                    </span>
                    <span className="text-white/20">·</span>
                    <span className="font-mono">last {relTime(a.last_run_at)}</span>
                    <span className="text-white/20">·</span>
                    <span className="font-mono">next {a.active ? relTime(a.next_run_at) : "paused"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => toggle(a)}
                    aria-label={a.active ? "Pause" : "Resume"}
                    title={a.active ? "Pause" : "Resume"}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.06] transition-colors"
                  >
                    {a.active ? <Pause size={15} /> : <Play size={15} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => del(a)}
                    aria-label="Delete"
                    title="Delete"
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
